import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  extractIdsFromData,
  toggleSelectionUtil,
  toggleAllUtil,
  isAllSelectedUtil,
  isPartiallySelectedUtil,
  isSelectionDirtyUtil,
} from './selectionState.utils';

export interface UseSelectionStateReturn {
  currentSelections: string[];
  isDirty: boolean;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  isSelected: (id: string) => boolean;
  toggleSelection: (id: string) => void;
  toggleAll: () => void;
  resetSelections: () => void;
}

export function useSelectionState<TData = unknown>(
  data: TData[],
  idKey: keyof TData,
  initialSelections: string[]
): UseSelectionStateReturn {
  const [currentSelections, setCurrentSelections] =
    useState<string[]>(initialSelections);

  useEffect(() => {
    setCurrentSelections(initialSelections);
  }, [initialSelections]);

  const isSelected = useCallback(
    (id: string): boolean => currentSelections.includes(id),
    [currentSelections]
  );

  const toggleSelection = useCallback((id: string): void => {
    setCurrentSelections((prev) => toggleSelectionUtil(prev, id));
  }, []);

  const toggleAll = useCallback((): void => {
    const visibleIds = extractIdsFromData(data, idKey);
    setCurrentSelections((prev) => toggleAllUtil(prev, visibleIds));
  }, [data, idKey]);

  const resetSelections = useCallback((): void => {
    setCurrentSelections(initialSelections);
  }, [initialSelections]);

  const isDirty = useMemo(
    () => isSelectionDirtyUtil(currentSelections, initialSelections),
    [currentSelections, initialSelections]
  );

  const visibleIds = useMemo(
    () => extractIdsFromData(data, idKey),
    [data, idKey]
  );

  const isAllSelected = useMemo(
    () => isAllSelectedUtil(currentSelections, visibleIds),
    [currentSelections, visibleIds]
  );

  const isPartiallySelected = useMemo(
    () => isPartiallySelectedUtil(currentSelections, visibleIds),
    [currentSelections, visibleIds]
  );

  return {
    currentSelections,
    isDirty,
    isAllSelected,
    isPartiallySelected,
    isSelected,
    toggleSelection,
    toggleAll,
    resetSelections,
  };
}
